import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import {MUIButton, MUIContainer} from '@/components/MUI-Components';
import {dateConvertor} from '@/components/Common/dateconverter';
import skelton from 'public/images/blog/skelton.webp';
import {formatListingDate} from '@/util/blogDates';
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));

interface RelatedPost {
    id: any;
    slug: string;
    title: any; // string for blogs, {rendered: string} for use-cases
    date: string;
    blog_image?: string; // legacy field (listing page)
    featured_image_url?: string; // API docs field (detail page related posts)
    yoast_head_json?: {og_image?: Array<{url: string}>}; // use-cases
}

interface RelatedPostsProps {
    items: RelatedPost[];
    currentId: any;
    /** Base path for links: '/blog' or '/use-cases' */
    basePath: string;
    /** Section heading shown above the cards */
    heading: string;
}

/**
 * Reusable related-posts / latest-insights card grid.
 * Used by both blog/[blogsDetails].tsx and use-cases/[useCase].tsx via basePath prop.
 */
const RelatedPosts = ({items, currentId, basePath, heading}: RelatedPostsProps) => {
    const filtered = items?.filter((item) => item.id !== currentId).slice(0, 3);
    if (!filtered?.length) return null;

    return (
        <Box className="margin top-spacing">
            <Box className="spectrum-header-wrapper" pt={2}>
                <Typography className="spectrum-heading color-black">{heading}</Typography>
                <Box className="divder-wrapper">
                    <hr className="divider-line" />
                </Box>
            </Box>
            <MUIContainer>
                <Grid container spacing={3} paddingBottom={3}>
                    {filtered?.map((item) => {
                        // Support both blog (string title) and use-case ({rendered} title) shapes
                        const titleStr = typeof item.title === 'string' ? item.title : item.title?.rendered;
                        const imgSrc = item.featured_image_url || item.blog_image || item.yoast_head_json?.og_image?.[0]?.url || skelton;

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={4} className="insight_grid" key={item.id}>
                                <Box className="insights_card">
                                    <Link
                                        href={`${basePath}/${item.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="menu-links"
                                    >
                                        <DisplayImage
                                            src={imgSrc}
                                            alt="blog-image"
                                            height={218}
                                            width={300}
                                            loading="lazy"
                                            className="insights_img"
                                        />
                                    </Link>
                                    <Box className="content">
                                        <Box className="date_icon">
                                            <Typography className="insights_date">{formatListingDate(item, dateConvertor)}</Typography>
                                        </Box>
                                        <Tooltip title={titleStr} placement="top-start" arrow>
                                            <div>
                                                <Link
                                                    href={`${basePath}/${item.slug}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="card-redirection-heading"
                                                >
                                                    <Typography className="insights_card_heading">{titleStr}</Typography>
                                                </Link>
                                            </div>
                                        </Tooltip>
                                        <Box className="spectrum-btn">
                                            <Link
                                                href={`${basePath}/${item.slug}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="menu-links"
                                            >
                                                <MUIButton
                                                    label="Read More"
                                                    variant="contained"
                                                    className="insights_btn"
                                                    aria-label="Read More"
                                                />
                                            </Link>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(RelatedPosts);

interface InlineRelatedPostProps {
    item: RelatedPost & {unique_categories?: Array<{name: string} | string>};
    basePath: string;
    categoryLabel?: string;
}

/** Inline related post card — uses same CSS classes as the Related Articles sidebar section. */
const InlineRelatedPostBase = ({item, basePath, categoryLabel = ''}: InlineRelatedPostProps) => {
    const titleStr = typeof item.title === 'string' ? item.title : item.title?.rendered;
    const imgSrc = item.featured_image_url || item.blog_image || item.yoast_head_json?.og_image?.[0]?.url || skelton;

    return (
        <Box className="bdn-related-section">
            <Box className="bdn-related-section__list">
                <Link href={`${basePath}/${item.slug}`} target="_blank" rel="noopener noreferrer" className="bdn-related-section__item">
                    <Box className="bdn-related-section__thumb">
                        <DisplayImage src={imgSrc} alt={titleStr ?? 'related post'} width={160} height={110} loading="lazy" />
                    </Box>
                    <Box className="bdn-related-section__content">
                        <span className="bdn-related-section__category">{categoryLabel || 'Blog'}</span>
                        <Typography className="bdn-related-section__title">{titleStr}</Typography>
                    </Box>
                    <span className="bdn-related-section__arrow">&#8594;</span>
                </Link>
            </Box>
        </Box>
    );
};
InlineRelatedPostBase.displayName = 'InlineRelatedPost';
export const InlineRelatedPost = memo(InlineRelatedPostBase);
